import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PaymentEvent, Transaction } from '../api/types';
import { payments, type PaymentEvent as PaymentEventModel } from '../api/client';
import { BillViewer } from './BillViewer';
import { BillDropzone } from './BillDropzone';
import { PaymentEventForm, type PaymentPrefill } from './PaymentEventForm';
import { MovePaymentForm } from './MovePaymentForm';
import { ConversionLegModal } from './ConversionLegModal';
import { useToast } from './Toast';
import { Badge, Button, Drawer, Provenance } from './ui';
import { formatAmount, formatCompact, formatDateTime } from '../lib/format';

const TYPE_LABEL: Record<Transaction['type'], string> = {
  PAYMENT: 'Payment',
  CONVERSION: 'Conversion',
  TOPUP: 'Top-up',
};

const TYPE_ICON: Record<Transaction['type'], string> = {
  PAYMENT: '→',
  CONVERSION: '⇄',
  TOPUP: '＋',
};

const FLOW_LABEL: Record<PaymentEvent['flow'], string> = {
  DIRECT: 'A — direct conversion',
  TWO_STEP: 'B — two-step at the bank',
  TOPUP_BALANCE: 'C — card top-up',
};

/**
 * Whether the event already carries a leg whose rubles count (`computeRubTotal`
 * sums the CONVERSION and TOPUP legs) — no second one can be added.
 */
function hasRubLeg(event: PaymentEventModel): boolean {
  return event.transactions.some((txn) => txn.type === 'CONVERSION' || txn.type === 'TOPUP');
}

function TransactionCard({
  txn,
  onOpenBill,
  defaultOpen,
  onEditAccount,
  onDelete,
  confirmingDelete,
  onConfirmDelete,
  onCancelDelete,
  deleting,
}: {
  txn: Transaction;
  onOpenBill: (billId: string) => void;
  /** The only leg of a payment with no bill yet starts unfolded, so the upload
   *  action is discoverable without a second, competing dropzone. */
  defaultOpen?: boolean;
  /** Re-point the account this leg is booked on. */
  onEditAccount: () => void;
  onDelete: () => void;
  /** Two-step confirm: the leg's rubles leave the ₽ total the moment it goes. */
  confirmingDelete?: boolean;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  deleting?: boolean;
}) {
  return (
    <article className="txn">
      <header className="txn__head">
        <span className="txn__type" title={TYPE_LABEL[txn.type]}>
          <span aria-hidden="true">{TYPE_ICON[txn.type]}</span>
          {TYPE_LABEL[txn.type]}
        </span>
        {txn.sourceTxnNo ? <Badge>№{txn.sourceTxnNo}</Badge> : null}
        <span className="txn__amount">{formatAmount(txn.amount, txn.currency)}</span>
      </header>
      <dl className="dl">
        <dt>Account</dt>
        <dd>{txn.account ? `${txn.account.name} (${txn.account.currency})` : txn.accountId}</dd>
        <dt>Occurred</dt>
        <dd>{formatDateTime(txn.occurredAt)}</dd>
        {txn.fxRate !== null ? (
          <>
            <dt>FX rate</dt>
            <dd>
              {txn.fxRate}{' '}
              {txn.rateSource ? <Badge tone={txn.rateSource === 'MANUAL' ? 'warn' : 'brand'}>{txn.rateSource}</Badge> : null}
            </dd>
          </>
        ) : null}
        {txn.rubAmount !== null ? (
          <>
            <dt>RUB</dt>
            <dd>{formatAmount(txn.rubAmount, 'RUB')}</dd>
          </>
        ) : null}
        {txn.commission !== null ? (
          <>
            <dt>Commission</dt>
            <dd>{formatAmount(txn.commission, txn.currency)}</dd>
          </>
        ) : null}
      </dl>
      {txn.bills.length > 0 ? (
        <div className="bills">
          {txn.bills.map((bill) => (
            <button type="button" className="bill-thumb" key={bill.id} onClick={() => onOpenBill(bill.id)}>
              {bill.originalName ?? bill.id.slice(0, 8)}
            </button>
          ))}
        </div>
      ) : null}
      {/* The only upload affordance in the drawer, targeted at the leg it
          belongs to. The payment-level dropzone this replaced always wrote to
          the first leg no matter which one was on screen. */}
      <details open={defaultOpen} style={{ marginTop: 'var(--space-2)' }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
          {txn.bills.length > 0 ? 'Attach another bill to this leg' : 'Attach a bill to this leg'}
        </summary>
        <div style={{ marginTop: 'var(--space-2)' }}>
          <BillDropzone transactionId={txn.id} compact />
        </div>
      </details>

      {/* The leg's own maintenance: which account it is booked on, and removing
          it again (the ₽ total is re-derived either way). */}
      <div className="txn__actions">
        {confirmingDelete ? (
          <>
            <span className="txn__confirm">Delete this leg and its rubles?</span>
            <Button variant="danger" size="sm" loading={deleting} onClick={onConfirmDelete}>
              Delete
            </Button>
            <Button variant="secondary" size="sm" onClick={onCancelDelete}>
              Keep
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onEditAccount}>
              Edit account
            </Button>
            <span style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" onClick={onDelete}>
              Delete leg
            </Button>
          </>
        )}
      </div>
    </article>
  );
}

/** Which panel the single drawer overlay is showing right now. */
type Panel = 'none' | 'view' | 'edit' | 'create' | 'move' | 'bill';

/** The leg-level overlay: open to add the conversion leg, or to re-point an account. */
type LegPanel = { mode: 'create' } | { mode: 'edit'; leg: Transaction } | null;

/**
 * Side drawer for one payment event: the legs with their rates, the rubles
 * actually spent and the bills. Opened from a matrix cell.
 *
 * Exactly one overlay at a time: the bill viewer, the edit form and the create
 * form each REPLACE the payment view instead of stacking on top of it, so there
 * is one scrim, one focus trap and one Esc level to close.
 */
export function PaymentDrawer({
  eventId,
  onClose,
  createOpen = false,
  onCreateClose,
  onCreated,
  createPrefill = null,
}: {
  eventId: string | null;
  onClose: () => void;
  /** Set by the page header's "Record payment" action. */
  createOpen?: boolean;
  onCreateClose?: () => void;
  /**
   * Called instead of `onClose` when a payment was created but its bill upload
   * failed: the payment stays, and the drawer switches to it so the leg's own
   * dropzone becomes the retry path.
   */
  onCreated?: (id: string) => void;
  /**
   * Set by the forecast drawer's "Register payment now": the form starts from
   * the forecast's subscription, amount and renewal date.
   */
  createPrefill?: PaymentPrefill | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [billId, setBillId] = useState<string | null>(null);
  /** The just-created payment whose bill upload failed. */
  const [billRetry, setBillRetry] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [legPanel, setLegPanel] = useState<LegPanel>(null);
  const [legToDelete, setLegToDelete] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['payments', eventId],
    queryFn: ({ signal }) => payments.get(eventId ?? '', signal),
    enabled: Boolean(eventId),
  });

  const remove = useMutation({
    mutationFn: (id: string) => payments.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast('Payment deleted', 'success');
      onClose();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const removeLeg = useMutation({
    mutationFn: (id: string) => payments.removeTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast('Leg deleted — rubles re-derived', 'success');
      setLegToDelete(null);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const data: PaymentEventModel | undefined = query.data;

  const panel: Panel = billId
    ? 'bill'
    : createOpen
      ? 'create'
      : moving
        ? 'move'
        : editing
          ? 'edit'
          : eventId
            ? 'view'
            : 'none';

  const closeAll = () => {
    setEditing(false);
    setMoving(false);
    setBillId(null);
    setBillRetry(null);
    setConfirmRemove(false);
    setLegPanel(null);
    setLegToDelete(null);
    onCreateClose?.();
    onClose();
  };

  /**
   * The leg modal is the ONE overlay above the drawer, so while it is open Esc
   * and the scrim only dismiss it — the payment stays underneath, the way the
   * bill viewer is the only other exception to "one overlay at a time".
   */
  const closeLegPanel = () => {
    setLegPanel(null);
    setLegToDelete(null);
  };

  const title = (() => {
    if (panel === 'create') return 'Record a payment';
    if (panel === 'edit') return 'Edit payment';
    if (panel === 'move') return 'Move to subscription…';
    return data ? `${formatAmount(data.paidAmount, data.paidCurrency)} — ${data.subscription?.name ?? ''}` : 'Payment';
  })();

  const description = (() => {
    if (panel === 'create') return 'The ruble total is derived from the transaction leg.';
    if (panel === 'edit') return data?.subscription?.name ?? undefined;
    if (panel === 'move') return 'The legs, bills and rubles stay put — only the parent changes.';
    return data ? `${formatDateTime(data.occurredAt)} · ${FLOW_LABEL[data.flow]}` : undefined;
  })();

  // The form owns Cancel/Save; the payment footer (Delete / Edit / Done) would
  // sit under it offering an "Edit" that is already happening.
  const showPaymentFooter = panel === 'view';

  // Entity provenance: the quiet footer line that answers "who recorded this".
  // It only stands alone when the action buttons do not.
  const provenance =
    panel === 'view' && data ? (
      <Provenance
        createdBy={data.createdBy}
        createdAt={data.createdAt}
        updatedBy={data.updatedBy}
      />
    ) : null;

  return (
    <>
      {(panel === 'view' || panel === 'edit' || panel === 'move') && eventId ? (
        <Drawer
          open
          onClose={legPanel ? closeLegPanel : closeAll}
          title={title}
          description={description}
          footer={
            showPaymentFooter ? (
              confirmRemove ? (
                <>
                  <span style={{ flex: 1, alignSelf: 'center', fontSize: 13, color: 'var(--danger-fg)' }}>
                    Delete this payment and its legs?
                  </span>
                  <Button variant="danger" size="sm" loading={remove.isPending} onClick={() => remove.mutate(eventId)}>
                    Delete
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmRemove(false)}>
                    Keep
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="danger" size="sm" onClick={() => setConfirmRemove(true)}>
                    Delete
                  </Button>
                  <span style={{ flex: 1 }} />
                  <Button variant="ghost" onClick={() => setMoving(true)}>
                    Move to subscription…
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                  <Button variant="primary" onClick={closeAll}>
                    Done
                  </Button>
                </>
              )
            ) : (
              provenance
            )
          }
        >
          {query.isPending ? (
            <p className="state">Loading…</p>
          ) : query.isError ? (
            <p className="state state--error" role="alert">
              {query.error.message}
            </p>
          ) : panel === 'edit' && data ? (
            <PaymentEventForm
              event={data}
              onDone={() => {
                setEditing(false);
                setConfirmRemove(false);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : panel === 'move' && data ? (
            <MovePaymentForm
              event={data}
              onDone={() => setMoving(false)}
              onCancel={() => setMoving(false)}
            />
          ) : data ? (
            <>
              <dl className="dl">
                <dt>Paid</dt>
                <dd>{formatAmount(data.paidAmount, data.paidCurrency)}</dd>
                <dt>RUB total</dt>
                <dd>
                  {data.rubTotal === null ? (
                    <span className="cell-muted">— no rubles derived yet</span>
                  ) : (
                    formatCompact(data.rubTotal, 'RUB')
                  )}
                </dd>
                <dt>Flow</dt>
                <dd>
                  {FLOW_LABEL[data.flow]}
                  {data.transactions.length > 1 ? <Badge tone="brand">multi-leg</Badge> : null}
                </dd>
                {data.note ? (
                  <>
                    <dt>Note</dt>
                    <dd>{data.note}</dd>
                  </>
                ) : null}
              </dl>

              <h3 className="section-title">Transactions ({data.transactions.length})</h3>

              {billRetry === data.id ? (
                <div className="bill-retry" role="alert">
                  <p className="bill-retry__text">
                    The payment is recorded, but its bill did not upload. Drop the file on the leg below to try again.
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setBillRetry(null)}>
                    Dismiss
                  </Button>
                </div>
              ) : null}

              {hasRubLeg(data) ? null : (
                <div className="conversion-invite">
                  {data.flow === 'TWO_STEP' ? (
                    <p className="conversion-invite__hint">
                      Two-step event — add the conversion leg to count ₽ total
                    </p>
                  ) : null}
                  <Button variant="secondary" size="sm" onClick={() => setLegPanel({ mode: 'create' })}>
                    Add conversion leg
                  </Button>
                </div>
              )}

              {data.transactions.map((txn) => (
                <TransactionCard
                  key={txn.id}
                  txn={txn}
                  onOpenBill={setBillId}
                  defaultOpen={data.transactions.length === 1 && txn.bills.length === 0}
                  onEditAccount={() => setLegPanel({ mode: 'edit', leg: txn })}
                  onDelete={() => setLegToDelete(txn.id)}
                  confirmingDelete={legToDelete === txn.id}
                  onConfirmDelete={() => removeLeg.mutate(txn.id)}
                  onCancelDelete={() => setLegToDelete(null)}
                  deleting={removeLeg.isPending && legToDelete === txn.id}
                />
              ))}
            </>
          ) : null}
        </Drawer>
      ) : null}

      {panel === 'create' ? (
        <Drawer open onClose={closeAll} title={title} description={description}>
          <PaymentEventForm
            event={null}
            prefill={createPrefill}
            onDone={(saved, options) => {
              onCreateClose?.();
              if (options?.uploadFailed && onCreated) {
                // Keep the drawer open on the new payment: the leg's dropzone
                // is the retry for the bill that just failed.
                setBillRetry(saved.id);
                onCreated(saved.id);
                return;
              }
              onClose();
            }}
            onCancel={closeAll}
          />
        </Drawer>
      ) : null}

      {panel === 'bill' ? <BillViewer billId={billId} onClose={closeAll} onBack={() => setBillId(null)} /> : null}

      {legPanel && data ? (
        <ConversionLegModal
          event={data}
          leg={legPanel.mode === 'edit' ? legPanel.leg : null}
          onClose={closeLegPanel}
          onSaved={(saved) => {
            queryClient.setQueryData(['payments', saved.id], saved);
            closeLegPanel();
          }}
        />
      ) : null}
    </>
  );
}
